/**
 * Auth model — 5-role RBAC + JWT claim shape.
 *
 * Source of truth: docs/deployment/medpod-sb/07-rbac-matrix.md
 *
 *   owner   — Admin UI + User UI; full powers + admin-user CRUD +
 *             desktop-license management (issue / revoke)
 *   manager — Admin UI + User UI; full powers EXCEPT licensing
 *             (cannot issue, see, or revoke desktop licenses).
 *             Useful for clinical-leadership users who need both
 *             surfaces but not commercial-ops powers.
 *   admin   — Admin UI only; user mgmt + study admin + audit read
 *   senior  — User UI only; clinical reading + report override
 *   junior  — User UI only; clinical reading + own reports only
 *
 * Per-row "own work only" (matrix ⊕) is enforced at the SQL layer in
 * the Zig service via the assignee join — not in this matrix.
 *
 * Owner-target + self-target RBAC:
 *   No actor can manage their OWN row from the admin Users
 *   tab (denyIfSelfTarget) — self-management goes through a
 *   personal-profile flow when signed in, or the "Reset
 *   password" link on the hosted sign-in page when not.
 *   The `manager` and `admin` roles can manage every other
 *   user but NOT the owner role (denyIfOwnerTarget); only an
 *   existing owner can grant or revoke the owner role;
 *   owner-on-owner password reset is email-only (no temp).
 *   Both gates live in src/server.ts.  See
 *   docs-official/books/REGULATORY.md §11.10(d) and
 *   docs-official/books/DEVELOPER.md Chapter 11 §5 for the full
 *   contract + audit-row mapping.
 */

export const PACS_ROLES = ["owner", "manager", "admin", "senior", "junior"] as const;

export type PacsRole = typeof PACS_ROLES[number];

export interface OidcClaims {
  iss: string;
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  exp: number;
  iat: number;
  /** Zitadel emits role grants under this exact claim name. */
  "urn:zitadel:iam:org:project:roles"?: Record<string, Record<string, string>>;
}

export interface AuthState {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  claims: OidcClaims;
  roles: PacsRole[];
  expiresAt: number;
}

export function extractRoles(claims: OidcClaims): PacsRole[] {
  const roleClaim = claims["urn:zitadel:iam:org:project:roles"] ?? {};
  const keys = Object.keys(roleClaim);
  return PACS_ROLES.filter((r) => keys.includes(r));
}

// -----------------------------------------------------------------------
//  Permission matrix
// -----------------------------------------------------------------------

export type PacsAction =
  // Auth surfaces
  | "ui.admin.login"
  | "ui.user.login"
  | "auth.logout"
  // User mgmt
  | "users.create"
  | "users.read"
  | "users.update"
  | "users.reset_pw"
  | "users.create_admin"
  // Self-service
  | "self.update_profile"
  | "self.update_pw"
  // Studies
  | "studies.read"
  | "studies.update_meta"
  | "studies.soft_delete"
  | "studies.purge"
  | "studies.assign"
  // Reports
  | "reports.draft"
  | "reports.amend"
  | "reports.sign"
  | "reports.override"
  // Audit / export / labels
  | "audit.read"
  | "reports.export"
  | "labels.export"
  // Bulk data export (images+videos+annotations+clinical) — owner only.
  | "data.export"
  // RBAC
  | "rbac.update"
  // Desktop licensing — owner only. Manager + admin + readers cannot
  // issue, view the key list, or revoke. Surface a 403 in the API,
  // hide the tab in the UI.
  | "licenses.read"
  | "licenses.issue"
  | "licenses.revoke";

const ALL_AUTHED: PacsRole[]   = ["owner", "manager", "admin", "senior", "junior"];
const STAFF: PacsRole[]        = ["owner", "manager", "admin"];                   // admin-UI roles
const READERS: PacsRole[]      = ["owner", "manager", "senior", "junior"];        // user-UI roles
const ADMIN_PLUS: PacsRole[]   = ["owner", "manager"];                            // both surfaces, both real
const OWNER_ONLY: PacsRole[]   = ["owner"];

const MATRIX: Record<PacsAction, PacsRole[]> = {
  "ui.admin.login":      STAFF,
  "ui.user.login":       READERS,
  "auth.logout":         ALL_AUTHED,
  "users.create":        STAFF,
  "users.read":          STAFF,
  "users.update":        STAFF,
  "users.reset_pw":      STAFF,
  "users.create_admin":  ADMIN_PLUS,
  "self.update_profile": ALL_AUTHED,
  "self.update_pw":      ALL_AUTHED,
  "studies.read":        ALL_AUTHED,
  "studies.update_meta": STAFF,
  "studies.soft_delete": STAFF,
  "studies.purge":       ADMIN_PLUS,
  "studies.assign":      STAFF,
  "reports.draft":       ["owner", "manager", "senior", "junior"],
  "reports.amend":       ["owner", "manager", "senior", "junior"],
  "reports.sign":        ["owner", "manager", "senior", "junior"],
  "reports.override":    ["owner", "manager", "senior"],
  "audit.read":          STAFF,
  "reports.export":      ALL_AUTHED,
  "labels.export":       STAFF,
  "data.export":         OWNER_ONLY,
  "rbac.update":         OWNER_ONLY,
  "licenses.read":       OWNER_ONLY,
  "licenses.issue":      OWNER_ONLY,
  "licenses.revoke":     OWNER_ONLY,
};

export function can(state: AuthState | null, action: PacsAction): boolean {
  if (!state) return false;
  return state.roles.some((r) => MATRIX[action].includes(r));
}

/** True iff the user can enter the Admin UI surface (/admin/*). */
export function canAdmin(state: AuthState | null): boolean {
  return can(state, "ui.admin.login");
}

/** True iff the user can enter the User UI surface (everything else). */
export function canUserUi(state: AuthState | null): boolean {
  return can(state, "ui.user.login");
}

/** True iff the user can manage desktop edge licenses (owner only). */
export function canManageLicenses(state: AuthState | null): boolean {
  return can(state, "licenses.read");
}
